/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { triggerRerender } from '../../../redux/rerenderSlice'
import { useNavigate, useLocation } from "react-router-dom"
import { selectModules } from '../../../redux/moduleSlice'
import { selectDropdowns, selectDropdownOptions } from '../../../redux/dropdownSlice'
import FormFieldBuilder from "../../../components/FormFieldBuilder";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle, faEye, faTrash } from '@fortawesome/free-solid-svg-icons'
import SpecialInstruction from '../../../components/elements/SpecialInstruction'
import Loading from '../../../components/Loading'
import Dropzone from 'react-dropzone';
import DisplayField from '../../../components/elements/DisplayField'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import ViewSensitivePiiButtons from "../../../components/ViewSensitivePiiButtons";
import { NOTE_BEARING_BK_DOCUMENT_TYPES } from '../../../constants'

export default function BankruptcyMonitoringModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const { register, handleSubmit, control, watch, formState: { errors }, reset, setValue } = useForm();
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const customerId = modal.options?.customerId
  const loanId = modal.options?.loanId
  const navi = useNavigate()
  const [module, setModule] = useState()
  const [moduleFields, setModuleFields] = useState()
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const [stateFields, setStateFields] = useState([])
  const modules = useSelector(selectModules)
  const watchBankruptcyStatusDropdownOptionId = watch('bankruptcy_status_dropdown_option_id')
  const [bankruptcyStatus, setBankruptcyStatus] = useState({})
  const [customerBankruptcy, setCustomerBankruptcy] = useState(false)
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search);
  const watchDocumentType = watch('document_type')
  const [isAgentConfirmed, setIsAgentConfirmed] = useState(false)
  const active = modal.is_active && modal.type === 'bankruptcy_attorney_information_modal'
  // Identifies the customer + loan an opening is for; late responses for any other opening are dropped.
  const requestKey = `${customerId}:${loanId}`
  const inFlightRequestKey = useRef(null)
  const [loadedRequestKey, setLoadedRequestKey] = useState(null)

  // there is no clear document type to get only for bankruptcy, so we are doing the substring check here
  const availableDocumentType = dropdownOptions?.filter((x) => (x?.code_key?.toLowerCase().includes('document_type_bankruptcy') || x?.code_key == 'document_type_poa' || x?.code_key == 'document_type_tpc_communication_order') && x?.is_active == 1 && x.optional_value_2 == "TRUE").map((o) => ({ label: o.name, value: o.id, codeKey: o.code_key }))
  const [bkFileUpload, setBkfileUpload] = useState(null)
  const [bkAllAddedFiles, setBkAllAddedFiles] = useState([])

  const onBkFileUploadDrop = (file) => setBkfileUpload(file)

  const fieldSet1 = ['bankruptcy_status_dropdown_option_id', 'bankruptcy_monitoring_status_dropdown_option_id']
  const fieldSet2 = ['debtor_law_firm', 'debtor_attorney_first_name', 'debtor_attorney_last_name', 'debtor_attorney_phone', 'debtor_attorney_email']
  const viewFields = ['case_number', 'chapter_number_dropdown_option_id', 'filing_date', 'discharge_date', 'dismissal_date', 'closed_date', 'reopened_date', 'vacate_date', 'withdrawal_date', 'claim_date', 'objection_date', 'debtor_state_dropdown_option_id']

  useEffect(() => {
    return () => {
      setCustomerBankruptcy(null);
    };
  }, []);

  // This modal stays mounted in the Footer and its fields take the loaded record only as
  // defaultValue (applied once at registration). Clear the previous customer on every open and drop
  // late responses, so a previous customer's attorney/case values can never become this one's.
  useEffect(() => {
    if (active && customerId) {
      resetState()
      getCustomerBankruptcyInfo(customerId)
    } else {
      inFlightRequestKey.current = null
    }
  }, [active, customerId, loanId])

  // The staged-document fields belong to the type selected when they were typed; carrying them
  // across a type switch saves them under the new type with no indication it happened.
  useEffect(() => {
    setValue('bk_title', '')
    setValue('bk_external_case_number', '')
    setValue('bk_document_date', '')
  }, [watchDocumentType])

  const addFile = () => {
    const isGeneralNotice = watchDocumentType?.codeKey === 'document_type_bankruptcy_general_notices'

    let isDuplicate = false
    if (!isGeneralNotice) {
      bkAllAddedFiles.forEach((item) => {
        if (watchDocumentType != null && item.type.codeKey == watchDocumentType.codeKey) {
          isDuplicate = true
        }
      })
    }

    if (isDuplicate) {
      dispatch(setNotification({ type: 'warning', body: 'You can only have one active document for each document type' }))
      return
    }
    if (watchDocumentType == null) {
      dispatch(setNotification({ type: 'warning', body: 'Please select a document type' }))
      return
    }
    if (bkFileUpload == null) {
      dispatch(setNotification({ type: 'warning', body: 'Please select a file' }))
      return
    }
    if (bkAllAddedFiles.length > 100) {
      dispatch(setNotification({ type: 'warning', body: 'You can only add up to 100 files' }))
      return
    }

    if (NOTE_BEARING_BK_DOCUMENT_TYPES.includes(watchDocumentType?.codeKey)) {
      const title = watch('bk_title')
      const externalCaseNumber = watch('bk_external_case_number')
      const documentDate = watch('bk_document_date')
      if (!externalCaseNumber || !documentDate) {
        dispatch(setNotification({ type: 'warning', body: 'LOX Resolve Case Number and Document Date are required for this document type' }))
        return
      }
      if (isGeneralNotice && !title) {
        dispatch(setNotification({ type: 'warning', body: 'Document Title is required for General Notices' }))
        return
      }

      setBkAllAddedFiles([...bkAllAddedFiles, {
        file: bkFileUpload[0],
        type: watchDocumentType,
        title: isGeneralNotice ? title : undefined,
        externalCaseNumber,
        documentDate,
      }])
      setBkfileUpload(null)
      setValue('document_type', null)
      setValue('bk_title', '')
      setValue('bk_external_case_number', '')
      setValue('bk_document_date', '')
      return
    }

    setBkAllAddedFiles([...bkAllAddedFiles, {
      file: bkFileUpload[0],
      type: watchDocumentType,
    }])
    setBkfileUpload(null)
    setValue('document_type', null)
  }

  const removeFile = (index) => {
    setBkAllAddedFiles(bkAllAddedFiles.filter((item, i) => i !== index))
  }

  const getCustomerBankruptcyInfo = async (customerId) => {
    const key = requestKey
    inFlightRequestKey.current = key
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bankruptcy_monitoring/${customerId}/attorney_information?loan_id=${loanId}`)
    try {
      const response = await request.get()
      if (inFlightRequestKey.current !== key) return
      setLoadedRequestKey(key)
      if (response.status === 200 || response.status === 201) {
        setCustomerBankruptcy(response?.data?.data)
        if (response?.data?.data?.bankruptcy_status_dropdown_option_id) {
          setBankruptcyStatus(dropdownOptions?.find((x) => x?.id == response?.data?.data?.bankruptcy_status_dropdown_option_id))
        }
      } else {
        setCustomerBankruptcy([])
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      if (inFlightRequestKey.current !== key) return
      setCustomerBankruptcy(null)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  useEffect(() => {
    if (watchBankruptcyStatusDropdownOptionId) {
      setBankruptcyStatus(dropdownOptions?.find((x) => x?.id == watchBankruptcyStatusDropdownOptionId))
    }
  }, [watchBankruptcyStatusDropdownOptionId])

  const resetState = () => {
    setCustomerBankruptcy(null)
    setLoadedRequestKey(null)
    setBankruptcyStatus({})
    setBkAllAddedFiles([])
    setBkfileUpload(null)
    setIsAgentConfirmed(false)
    setStateFields([])
    setSaving(false)
    reset()
  };

  const handleCancel = () => {
    resetState()
    dispatch(setModal({ is_active: false }))
  };

  const isLoaded = customerBankruptcy && loadedRequestKey === requestKey

  const handleStateFields = (name, value, module) => {
    const stateFieldName = `${module.code_key}_${name}`;
    if (stateFields.some((item) => item.name === stateFieldName)) {
      setStateFields(stateFields.map((item) => {
        if (stateFieldName === item?.name) {
          return {
            ...item,
            value: value
          }
        } else {
          return item
        }
      }))
    } else {
      setStateFields([
        ...stateFields,
        {
          name: stateFieldName,
          value: value
        }
      ])
    }
  }

  useEffect(() => {
    if (modules.data.length > 0) {
      const m = modules.data.find((e) => e.code_key === 'bankruptcy_monitoring')

      if (m) {
        setModule(m)

        const mf = [...m.module_fields]
        mf.sort((a, b) => a.detail_display_rank - b.detail_display_rank)
        setModuleFields(mf)
      }
    }
  }, [modules.data])

  const confirmBeforeFinalize = () => {
    dispatch(setModal({
      ...modal,
      extraOptions: {
        is_bankruptcy_finalize: true,
        type: 'bankruptcy_finalize_confirmation_modal',
        callMethod: () => {
          setIsAgentConfirmed(true);

          // Delay form re-submission to ensure the state update above is reflected.
          // We manually trigger the form's submit event, which causes react-hook-form's handleSubmit(onSubmit) to re-execute with the latest form state.
          setTimeout(() => {
            document.getElementById('bankruptcy_attorney_information_modal_form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }, 0);
        },
        closeMethod: () => setIsAgentConfirmed(false)
      }
    }));
  };

  /**
   * Handles the form submission for updating Attorney Information for Bankruptcy Monitoring
   */
  const onSubmit = async (formData) => {
    // check if the bankruptcy status is not finalized and if the discharge date is set
    if (bankruptcyStatus?.code_key != 'bankruptcy_status_finalized' && formData?.discharge_date) {
      dispatch(setNotification({ type: 'error', body: 'You can only enter a discharge date when setting the bankruptcy status to finalized' }))
      return
    }

    // check if the bankruptcy status is finalized and if the discharge date is not set
    if (bankruptcyStatus?.code_key == 'bankruptcy_status_finalized' && !formData?.discharge_date) {
      dispatch(setNotification({ type: 'error', body: 'You must enter a discharge date when setting the bankruptcy status to finalized' }))
      return
    }

    // check if the bankruptcy status is finalized and if the agent has confirmed
    if (bankruptcyStatus?.code_key == 'bankruptcy_status_finalized' && !isAgentConfirmed) {
      confirmBeforeFinalize();
      return;
    }

    if (bankruptcyStatus?.code_key == 'bankruptcy_status_confirmed') {
      if (!formData.debtor_law_firm && !formData.debtor_attorney_first_name && !formData.debtor_attorney_last_name && !formData.debtor_attorney_phone && !formData.debtor_attorney_email) {
        dispatch(setNotification({ type: 'error', body: 'Please fill in at least one attorney field' }))
        return
      }
    }

    setSaving(true)

    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bankruptcy_monitoring/${customerId}/attorney_information`)

    try {
      // Initialize the request payload
      let body = {}
      let file = []

      body = {
        debtor_law_firm: formData.debtor_law_firm,
        debtor_attorney_first_name: formData.debtor_attorney_first_name,
        debtor_attorney_last_name: formData.debtor_attorney_last_name,
        debtor_attorney_phone: formData.debtor_attorney_phone,
        debtor_attorney_email: formData.debtor_attorney_email,
        bankruptcy_status_dropdown_option_id: formData.bankruptcy_status_dropdown_option_id,
        filed_for_bankruptcy_is_active: formData.filed_for_bankruptcy_is_active,
        submit_for_monitoring_is_active: formData.submit_for_monitoring_is_active,
        power_of_attorney_customer_is_active: formData.power_of_attorney_customer_is_active,
        cease_and_desist_is_active: formData.cease_and_desist_is_active,
        case_number: formData.case_number,
        chapter_number_dropdown_option_id: formData.chapter_number_dropdown_option_id,
        filing_date: formData.filing_date,
        dismissal_date: formData.dismissal_date,
        document_status: formData.document_status,
      }

      if (formData?.discharge_date && bankruptcyStatus?.code_key == 'bankruptcy_status_finalized') {
        body.discharge_date = formData.discharge_date
      }

      if (bkAllAddedFiles.length > 0) {
        bkAllAddedFiles.forEach((fileItem) => {
          if (fileItem.file) {
            file.push({ file: fileItem.file, type: fileItem.type.codeKey })
          }
        })
      }

      bkAllAddedFiles.forEach((fileItem, idx) => {
        const i = idx + 1
        if (NOTE_BEARING_BK_DOCUMENT_TYPES.includes(fileItem.type?.codeKey)) {
          if (fileItem.type?.codeKey === 'document_type_bankruptcy_general_notices') {
            body[`title${i}`] = fileItem.title
          }
          body[`externalCaseNumber${i}`] = fileItem.externalCaseNumber
          body[`documentDate${i}`] = fileItem.documentDate
        }
      })

      if (loanId) {
        body.loan_id = loanId
      }

      // Make the API request
      const response = await request.modify(body, file)

      // If the operation was successful, execute the success callback and reset the form
      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        handleCancel()
        dispatch(setNotification({ type: 'success', body: response?.data?.message }))
        dispatch(triggerRerender({ subpanelId: 'bankruptcy_monitoring' }))
        dispatch(triggerRerender({ subpanelId: 'document' }))

        // reload the page
        const pathSegments = window.location.pathname.split('/');
        const moduleName = pathSegments[1];
        const loanGuid = queryParams.get('loan');
        const leadGuid = queryParams.get('lead');
        if (loanGuid) {
          setTimeout(() => navi(`/${moduleName}/${customerId}?loan=${loanGuid}`, { state: { reloadPage: true } }), 2000);
        } else if (leadGuid) {
          setTimeout(() => navi(`/${moduleName}/${customerId}?lead=${leadGuid}`, { state: { reloadPage: true } }), 2000);
        } else {
          setTimeout(() => navi(`/${moduleName}/${customerId}`, { state: { reloadPage: true } }), 2000);
        }
      } else {
        // If the operation was not successful, show an error notification
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      // If an exception occurs, reset the saving state and show an error notification
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const columnDataRender = () => {
    if (moduleFields && module) {
      const filteredView = moduleFields
        .filter((e) => viewFields.includes(e.column_name))
        .sort((a, b) => {
          return viewFields.indexOf(a.column_name) - viewFields.indexOf(b.column_name)
        });

      const renderDisplayField = (fields) => {
        return fields.map((m) => {
          if (['case_number', 'filing_date', 'chapter_number_dropdown_option_id', 'dismissal_date', 'discharge_date'].includes(m.column_name)) {
            const fieldType = dropdownOptions[m.module_field_type_dropdown_option_id]?.code_key
            let selectOptions = {
              dropdownOptions: dropdowns?.data.find((e) => m.dropdown_key === e.code_key)?.dropdown_options,
              is_editable: true,
              fieldType,
            };

            return (
              <FormFieldBuilder
                field={m}
                selectOptions={selectOptions}
                register={register}
                errors={errors}
                control={control}
                key={m.id}
                row={moduleFields}
                handleStateFields={(name, value) => { handleStateFields(name, value, module) }}
                stateFields={stateFields}
                data={customerBankruptcy[m.column_name]}
              />
            );
          } else {
            return (
              <DisplayField
                data={m}
                row={customerBankruptcy}
                key={m.id}
                dropdowns={dropdowns}
                dropdownOptions={dropdownOptions}
              />
            )
          }
        })
      }

      return (
        <>
          <div className="fields-container">
            <div className="column is-full py-2">
              <p className="subtitle is-4 has-text-weight-bold">
                Bankruptcy Details
              </p>
            </div>
            {renderDisplayField(filteredView)}
          </div>
          <div className="fields-container">
            <div className="column is-full py-2">
              <p className="subtitle is-4 has-text-weight-bold">
                Supporting Files <span
                  style={{ pointerEvents: 'auto' }}
                  className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                  data-tooltip={'Choose \'TPC Communication Order\' for any documentation instructing us to stop contacting the customer. This could contain Cease & Desist language.'}
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className={`pl-1 has-text-link`}
                  />
                </span>
              </p>
            </div>
          </div>

          <div className="fields-container">
            <div className="is-one-third-widescreen column py-2">
              <div className="fields-container" data-testid="bankruptcy-attorney-information-modal-document-type">
                <Controller
                  name="document_type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      inputId="bankruptcy-attorney-information-modal-document-type-input"
                      classNames={reactSelectClassNames()}
                      options={availableDocumentType}
                      styles={{
                        container: (base) => ({ ...base, width: '100%' }),
                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                      }}
                      menuPosition="fixed"
                    />
                  )}
                />
              </div>
            </div>
            <div className="is-one-third-widescreen column py-2">
              <div className="fields-container">
                <Dropzone onDrop={onBkFileUploadDrop}>
                  {({ getRootProps, getInputProps }) => (
                    <section className="container">
                      <div
                        {...getRootProps({ className: 'dropzone' })}
                        className="is-flex is-justify-content-center is-size-5 has-text-weight-bold fa-border"
                        style={{ borderColor: 'gray', borderStyle: 'dashed', backgroundColor: 'lightgray' }}
                      >
                        <input {...getInputProps()} data-testid="bankruptcy-attorney-information-modal-file-input" />
                        <p>Drop File</p>
                      </div>
                      <aside>
                        <ul>
                          <li className="is-size-7">{bkFileUpload && bkFileUpload[0]?.name}</li>
                        </ul>
                      </aside>
                    </section>
                  )}
                </Dropzone>
              </div>
            </div>
            <div className="is-one-third-widescreen column py-2">
              <div className="fields-container">
                <button type="button" className="button is-link is-small px-2" data-testid="bankruptcy-attorney-information-modal-add-file" onClick={() => addFile()}>Add</button>
              </div>
            </div>
          </div>

          {NOTE_BEARING_BK_DOCUMENT_TYPES.includes(watchDocumentType?.codeKey) && (
            <div className="fields-container">
              {watchDocumentType?.codeKey === 'document_type_bankruptcy_general_notices' && (
                <div className="is-one-third-widescreen column py-2">
                  <p className="has-text-grey is-small is-size-8">
                    <span className="mr-1">Document Title</span>
                    <span className="has-text-danger">*</span>
                  </p>
                  <input
                    type="text"
                    className="input is-small"
                    data-testid="bankruptcy-attorney-information-modal-document-title"
                    {...register('bk_title', { required: 'Document Title is required for General Notices' })}
                  />
                  <p className={`${errors.bk_title ? 'has-text-danger is-size-7' : 'is-hidden'} lox-capitalize-first-letter`}>
                    {errors.bk_title?.message}
                  </p>
                </div>
              )}
              <div className="is-one-third-widescreen column py-2">
                <p className="has-text-grey is-small is-size-8">
                  <span className="mr-1">LOX Resolve Case Number</span>
                  <span className="has-text-danger">*</span>
                </p>
                <input
                  type="text"
                  className="input is-small"
                  data-testid="bankruptcy-attorney-information-modal-external-case-number"
                  {...register('bk_external_case_number', { required: 'LOX Resolve Case Number is required for this document type' })}
                />
                <p className={`${errors.bk_external_case_number ? 'has-text-danger is-size-7' : 'is-hidden'} lox-capitalize-first-letter`}>
                  {errors.bk_external_case_number?.message}
                </p>
              </div>
              <div className="is-one-third-widescreen column py-2">
                <p className="has-text-grey is-small is-size-8">
                  <span className="mr-1">Document Date</span>
                  <span className="has-text-danger">*</span>
                </p>
                <input
                  type="date"
                  className="input is-small"
                  data-testid="bankruptcy-attorney-information-modal-document-date"
                  {...register('bk_document_date', { required: 'Document Date is required for this document type' })}
                />
                <p className={`${errors.bk_document_date ? 'has-text-danger is-size-7' : 'is-hidden'} lox-capitalize-first-letter`}>
                  {errors.bk_document_date?.message}
                </p>
              </div>
            </div>
          )}

          <SpecialInstruction
            content={'<span>' +
              '<span class=\'has-text-weight-bold\'>NOTE: You can only have one active document for each document type, with the exception of General Notices — multiple General Notices may be uploaded and all remain active. If you add a non-General-Notice document with the same type as an active document listed below, the original will be marked as inactive, but will still be viewable in the document subpanel. </span>' + '<br>' +
              '<span>If you added the wrong document, click the trashcan next to the document and you can then select that document type from the dropdown.</span>' +
              ' </span>'}
            type="is-warning"
          />

          {bkAllAddedFiles?.map((document, index) => {
            return (
              <div className="fields-container" key={document.id}>
                <div className="is-widescreen column py-2">
                  <div className="fields-container">
                    <div className="is-half-widescreen column py-2 pl-0">
                      {document?.type ? (
                        <div className="column">
                          <div className="columns is-vcentered">
                            <div className="colmun">
                              <p>
                                <a
                                  className="button is-primary"
                                  href={document?.s3_link}
                                  style={{ height: '2rem', width: '2rem' }}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </a> {document?.type?.label}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className={`is-one-quarter-widescreen column py-2`}>
                      <button className="button is-danger" onClick={() => removeFile(index)} style={{ height: '2rem', width: '2rem' }}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="fields-container">
            <div className="column is-full py-2">
              <p className="subtitle is-6 has-text-weight-bold">Active Documents</p>
            </div>
          </div>

          {customerBankruptcy?.documents?.filter((d) => d.is_active == 1).map((document, index) => {
            return (
              <div className="fields-container" key={document.id}>
                <div className="is-widescreen column py-2">
                  <div className="fields-container">
                    <div className="is-half-widescreen column py-2 pl-0">
                      {document?.id ? (
                        <div className="column">
                          <div className="columns is-vcentered">
                            <div className="colmun">
                              <p>
                                <ViewSensitivePiiButtons
                                  customerId={customerId}
                                  documentId={document.id}
                                  documentTypeId={document.document_type_dropdown_option_id}
                                  type={'bankruptcy_document'}
                                  style={{ left: '-10px' }}
                                  tooltipPosition={'right'}
                                /> {document?.document_type_ddo?.name}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className={`is-one-quarter-widescreen column py-2`}>
                      <label>
                        <Controller
                          name={`document_status[${index}].is_active`}
                          control={control}
                          defaultValue={document.is_active ? 1 : 0}
                          render={({ field }) => (
                            <input
                              {...field}
                              className="checkbox is-small"
                              type="checkbox"
                              checked={field.value === 1}
                              onChange={(e) => {
                                field.onChange(e.target.checked ? 1 : 0)
                              }}
                            />
                          )}
                        />
                        <input
                          type="hidden"
                          {...register(`document_status[${index}].id`)}
                          value={document.id}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )
    }
  }

  const renderBankruptcyForm = () => {
    if (moduleFields && module) {
      const filterAndSortByCustomFields = (moduleFields, customerFields) => {
        return moduleFields
          .filter((e) => customerFields.includes(e.column_name))
          .sort((a, b) => {
            return customerFields.indexOf(a.column_name) - customerFields.indexOf(b.column_name)
          })
      }

      const filteredFields1 = filterAndSortByCustomFields(moduleFields, fieldSet1)
      const filteredFields2 = filterAndSortByCustomFields(moduleFields, fieldSet2)

      const renderFields = (fields) => {
        return fields.map((m, index) => {
          const fieldType = dropdownOptions[m.module_field_type_dropdown_option_id]?.code_key
          let selectOptions = {
            dropdownOptions: dropdowns?.data.find((e) => m.dropdown_key === e.code_key)?.dropdown_options,
            is_editable: true,
            fieldType,
          };

          if (m.column_name == "bankruptcy_monitoring_status_dropdown_option_id") {
            return (
              <div key={m.id} className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column py-2">
                <p className="has-text-grey is-small is-size-8"><span className="mr-1">Bankruptcy Monitoring Status</span></p>
                <p>Active</p>
              </div>
            )
          }

          if (m.column_name == "bankruptcy_status_dropdown_option_id" && customerBankruptcy && customerBankruptcy?.length == 0) {
            selectOptions.dropdownOptions = selectOptions?.dropdownOptions?.filter((x) => !(["bankruptcy_status_none", "bankruptcy_status_finalized"].includes(x?.code_key)))
          }

          let field = { ...m };
          // if (field.column_name == 'debtor_law_firm') {
          //   field.is_required = true;
          // }

          return (
            <FormFieldBuilder
              field={field}
              selectOptions={selectOptions}
              register={register}
              errors={errors}
              control={control}
              key={field.id}
              row={moduleFields}
              handleStateFields={(name, value) => { handleStateFields(name, value, module) }}
              stateFields={stateFields}
              data={customerBankruptcy[field.column_name]}
            />
          );
        });
      };

      return (
        <>
          <p>This is used if customer claims bankruptcy or has proof of bankruptcy. Note as well that we have Phin
            monitoring all active loans in the system for bankruptcy details.</p>
          <div className="fields-container">
            {renderFields(filteredFields1)}
          </div>
          <div className="fields-container">
            <div className="column is-full py-2">
              <p className="subtitle is-4 has-text-weight-bold">
                Attorney Details <span
                  style={{ pointerEvents: 'auto' }}
                  className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                  data-tooltip={'Any of these fields that are filled in will override the customer\'s field of the same type throughout the system.'}
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className={`pl-1 has-text-link`}
                  />
                </span>
              </p>
            </div>
            {renderFields(filteredFields2)}
          </div>
        </>
      );
    } else {
      return <Loading />
    }
  }

  // eslint-disable-next-line no-nested-ternary
  return active ? (
    // eslint-disable-next-line no-nested-ternary
    <form onSubmit={handleSubmit(onSubmit)} id="bankruptcy_attorney_information_modal_form" className="lox-form" data-testid="bankruptcy-attorney-information-modal">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card modal-card-dmc column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Bankruptcy Attorney Information
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: '110px' }}
          >
            {!isLoaded && (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  <span className="has-text-center"><Loading /></span>
                </div>
              </div>
            )}
            {isLoaded && Object.keys(customerBankruptcy)?.length >= 0 && (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  {renderBankruptcyForm()}
                  {columnDataRender()}
                </div>
                {(bankruptcyStatus?.code_key == 'bankruptcy_status_confirmed') ? (
                  <div className="column">
                    <SpecialInstruction
                      content={'<span>' +
                        '<span class=\'has-text-weight-bold\'>This will perform the following actions:</span>' + '<br>' +
                        '<span class=\'pl-2\'>• Update related bankruptcy monitoring record</span> <br> ' +
                        '<span class=\'pl-2\'>• Any attorney info provided will replace the customer\'s for the duration of this loan</span> <br> ' +
                        '<span class=\'pl-2\'>• IF PoA document uploaded, will create a power of attorney record for this loan ONLY</span> <br> ' +
                        '<span class=\'pl-2\'>• Set customer to Do Not Call</span> <br> ' +
                        '<span class=\'pl-2\'>• This will remove any transactional or marketing emails that have been created but not yet sent</span> <br> ' +
                        '<span class=\'pl-2\'>• Set payment method for existing digital payments to personal check</span> <br> ' +
                        '<span class=\'pl-2\'>• Set ACH revocation</span> <br> ' +
                        '<span class=\'pl-2\'>• Prevent the customer from scheduling payments is SSP</span> <br> ' +
                        '<span class=\'pl-2\'>• Restrict who can create or edit payments for this customer</span> <br> ' +
                        '<span class=\'pl-2\'>• Disable debit card</span> <br> ' +
                        '<span class=\'pl-2\'>• Display Confirmed Bankruptcy Banner</span> <br> ' +
                        ' </span>'}
                      type="is-warning"
                    />
                  </div>
                ) : bankruptcyStatus?.code_key == 'bankruptcy_status_unconfirmed' ? (
                  <div className="column">
                    <SpecialInstruction
                      content={'<span>' +
                        '<span class=\'has-text-weight-bold\'>This will perform the following actions:</span>' + '<br>' +
                        '<span class=\'pl-2\'>• Display Unconfirmed Bankruptcy Banner</span> <br> ' +
                        " </span>"}
                      type="is-warning"
                    />
                  </div>
                ) : bankruptcyStatus?.code_key == 'bankruptcy_status_none' ? (
                  <div className="column">
                    <SpecialInstruction
                      content={'<span>' +
                        '<span class=\'has-text-weight-bold\'>This will perform the following actions:</span>' + '<br>' +
                        '<span class=\'pl-2\'>• Update related bankruptcy monitoring record</span> <br> ' +
                        '<span class=\'pl-2\'>• This will remove protections and restrictions placed on this account because of a bankruptcy:</span> <br> ' +
                        '<span class=\'pl-4\'>o Set related PoA record (if applicable) to inactive</span> <br> ' +
                        '<span class=\'pl-4\'>o Set related C&D record (if applicable) to inactive</span> <br> ' +
                        '<span class=\'pl-4\'>o Customer\'s contact details will be used for outreach again</span> <br> ' +
                        '<span class=\'pl-4\'>o Bankruptcy banner is removed</span> <br> ' +
                        '<span class=\'pl-4\'>o Allow customers to schedule payments in SSP</span> <br> ' +
                        '<span class=\'pl-4\'>o Remove restriction on who can create or edit payments in LOX</span> <br> ' +
                        '<span class=\'pl-4\'>o Set DNC values back to what they were prior to being set to BK</span> <br> ' +
                        '<span class=\'pl-4\'>o Update ACH revocation reason</span> <br> ' +
                        " </span>"}
                      type="is-warning"
                    />
                  </div>
                ) : bankruptcyStatus?.code_key == 'bankruptcy_status_finalized' ? (
                  <div className="column">
                    <SpecialInstruction
                      content={'<span>' +
                        '<span class=\'has-text-weight-bold\'>This will perform the following actions:</span>' + '<br>' +
                        '<span class=\'pl-2\'>• Waive remaining payments</span> <br> ' +
                        '<span class=\'pl-2\'>• Close the loan setting status to Paid off and Sub status to Bankruptcy</span> <br> ' +
                        "<span class='pl-2'>• Update related bankruptcy monitoring record</span> <br> " +
                        "<span class='pl-2'>• Remove the bankruptcy banner</span> <br> " +
                        "<span class='pl-2'>• This will remove protections and restrictions placed on this account because of a bankruptcy:</span> <br> " +
                        '<span class=\'pl-4\'>o Set related PoA record (if applicable) to inactive</span> <br> ' +
                        '<span class=\'pl-4\'>o Set related C&D record (if applicable) to inactive</span> <br> ' +
                        '<span class=\'pl-4\'>o Customer\'s contact details will be used for outreach again</span> <br> ' +
                        '<span class=\'pl-4\'>o Set DNC values back to what they were prior to being set to BK</span> <br> ' +
                        '<span class=\'pl-4\'>o Update ACH revocation reason</span> <br> ' +
                        " </span>"}
                      type="is-warning"
                    />
                  </div>
                ) : null}
              </div>
            )}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="button"
                className="button is-small has-background-grey has-text-white"
                onClick={handleCancel}
                data-testid="bankruptcy-attorney-information-modal-cancel"
              >
                Cancel
              </button>
              <button type="submit" className="button is-link is-small" disabled={saving && 'disabled'} data-testid="bankruptcy-attorney-information-modal-save">{saving ? (<><Loading size="is-size-7" /> Saving</>) : 'Save'}</button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
